import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';
import { Usuario } from '../../login/usuario';
import { Router } from '@angular/router';
import { TreinoUsuarioService } from '../../../../services/treino-usuario.service';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-treino',
  templateUrl: './treino.component.html',
  styleUrls: ['./treino.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TreinoComponent implements OnInit {

  form!: FormGroup;

  treinoId = 0;
  usuarioId = 0;

  exercicios: any[] = [];
  abdominais: any[] = [];
  cardios: any[] = [];
  treino: any;
  semTreino = false;
  carregando = true;

  user: Usuario | null = null;

  tiposExercicio = [
    { id: 1, nome: 'Perna' },
    { id: 2, nome: 'Bíceps' },
    { id: 3, nome: 'Tríceps' },
    { id: 4, nome: 'Peito' },
    { id: 5, nome: 'Costas' },
    { id: 6, nome: 'Ombro' }
  ];

  constructor(
    private fb: FormBuilder,
    private authorizeService: AuthService,
    private treinoUsuarioService: TreinoUsuarioService,
    private router: Router,
    private toastService: ToastService,
    @Inject('BASE_URL') private baseUrl: string,
  ) { }

  ngOnInit() {
    this.user = this.authorizeService.getUser();

    if (!this.user) {
      this.router.navigate(['/login'], {
        queryParams: { retUrl: '/treino' }
      });
      return;
    }

    this.usuarioId = this.user.id;
    this.loadTreino();
  }

  // 🔐 chave única por usuário + treino
  get storageKey(): string {
    return `treino_${this.usuarioId}_${this.treinoId}`;
  }

  loadTreino() {
    const diaSemana = new Date().getDay();

    this.carregando = true;
    this.semTreino = false;

    this.treinoUsuarioService.getAllByUserAndDiaSemana(diaSemana)
      .subscribe({
        next: res => {
          this.carregando = false;

          // sem treino configurado para hoje
          if (!res || !res.id) {
            this.semTreino = true;
            this.treino = null;
            this.exercicios = [];
            this.abdominais = [];
            this.cardios = [];
            return;
          }

          this.treino = res;
          this.treinoId = res.id;

          // EXERCÍCIOS
          this.exercicios = (res.treinoExercicios ?? []).map((te: any) => ({
            exercicioId: te.exercicioId,
            nome: te.exercicio.nome,
            tipo: this.tiposExercicio.find(t => t.id === te.exercicio.tipo)?.nome ?? 'Outros',
            cargaInicial: Number(te.cargaInicial ?? 1), // 🔥 força número
            series: te.series,
            repeticoes: te.repeticoes,

            video: te.exercicio.video
              ? `https://www.youtube.com/embed/${te.exercicio.video}`
              : null,

            imagens: te.exercicio.exercicioImagens?.map((img: any) =>
              this.buildExercicioImageUrl(img.fileServerName)
            ) || []
          }));

          // ABDOMINAIS
          this.abdominais = (res.treinoAbdominais ?? []).map((ta: any) => ({
            abdominalId: ta.abdominalId,
            nome: ta.abdominal.nome
          }));

          // CARDIO
          this.cardios = (res.treinoCardios ?? []).map((tc: any) => ({
            id: tc.cardioMetaId,
            nome: tc.cardioMeta.nome,
            distanciaAlvoKm: tc.cardioMeta.distanciaAlvoKm
          }));

          this.buildForm();
          this.restoreForm();
          this.listenChanges();
        },
        error: () => {
          this.carregando = false;
          this.semTreino = true;
        }
      });
  }

  buildForm() {
    this.form = this.fb.group({
      exercicios: this.fb.array(this.exercicios.map(e => this.fb.group({
        exercicioId: [e.exercicioId],
        cargaEfetuada: [this.normalizeCarga(e.cargaInicial)]
      }))),

      abdominais: this.fb.array(this.abdominais.map(a => this.fb.group({
        abdominalId: [a.abdominalId],
        series: [3, Validators.required],
        repeticoes: [15, Validators.required]
      }))),

      cardios: this.fb.array(this.cardios.map(c => this.fb.group({
        cardioMetaId: [c.id],
        distanciaKm: [Number(c.distanciaAlvoKm ?? 0)],
        tempoMinutos: [0],
        tipoTreino: ['leve']
      })))
    });
  }

  normalizeCarga(value: number): number {
    return Math.round((Number(value) || 0) * 2) / 2;
  }

  buildExercicioImageUrl(fileServerName: string): string {
    if (!fileServerName) {
      return '';
    }

    if (/^https?:\/\//i.test(fileServerName)) {
      return fileServerName;
    }

    const normalizedBaseUrl = this.baseUrl.endsWith('/') ? this.baseUrl : `${this.baseUrl}/`;
    return `${normalizedBaseUrl}uploads/exercicios/${fileServerName}`;
  }

  // 🔥 ESSENCIAL → mantém step 0.5 funcionando
  listenChanges() {
    this.form.valueChanges.subscribe(val => {

      const parsed = {
        ...val,
        exercicios: val.exercicios.map((e: any) => ({
          ...e,
          cargaEfetuada: parseFloat(e.cargaEfetuada)
        })),
        cardios: val.cardios.map((c: any) => ({
          ...c,
          distanciaKm: parseFloat(c.distanciaKm),
          tempoMinutos: parseInt(c.tempoMinutos)
        }))
      };

      localStorage.setItem(this.storageKey, JSON.stringify(parsed));
    });
  }

  restoreForm() {
    const saved = localStorage.getItem(this.storageKey);
    if (!saved) return;

    const parsed = JSON.parse(saved);

    // 🔥 garante números após restore
    parsed.exercicios?.forEach((e: any) => {
      e.cargaEfetuada = this.normalizeCarga(e.cargaEfetuada);
    });

    parsed.cardios?.forEach((c: any) => {
      c.distanciaKm = parseFloat(c.distanciaKm);
      c.tempoMinutos = parseInt(c.tempoMinutos);
    });

    this.form.patchValue(parsed);
  }

  // GETTERS
  get exerciciosFA() { return this.form.get('exercicios') as FormArray; }
  get abdominaisFA() { return this.form.get('abdominais') as FormArray; }
  get cardiosFA() { return this.form.get('cardios') as FormArray; }

  // RANGE
  getBaseCarga(i: number): number {
    const base = this.exercicios[i]?.cargaInicial;
    return (base === null || base === undefined) ? 1 : Number(base);
  }

  getMinCarga(i: number): number {
    return 0;
  }

  getMaxCarga(i: number): number {
    return Infinity;
  }

  onRangeChange(event: Event, index: number) {
    const value = parseFloat((event.target as HTMLInputElement).value);

    // força múltiplos de 0.5 (anti bug de precisão)
    const normalized = Math.round(value * 2) / 2;

    this.exerciciosFA.at(index).get('cargaEfetuada')?.setValue(normalized, {
      emitEvent: true
    });
  }

  // STEPPER (mobile-friendly) com press-and-hold
  private readonly cargaStep = 0.5;
  private holdTimeout: any = null;
  private holdInterval: any = null;

  startHold(event: Event, index: number, direction: 1 | -1) {
    event.preventDefault();

    // primeiro incremento imediato (equivale ao clique)
    this.changeCarga(index, direction * this.cargaStep);

    // depois de 400ms começa a repetir; acelera após alguns ticks
    this.holdTimeout = setTimeout(() => {
      let ticks = 0;
      const tick = () => {
        ticks++;
        // após 8 ticks, acelera (passo duplo)
        const step = this.cargaStep * (ticks > 8 ? 2 : 1);
        this.changeCarga(index, direction * step);
        // intervalo dinâmico: começa em 120ms e desce até 60ms
        const delay = ticks > 8 ? 60 : 120;
        this.holdInterval = setTimeout(tick, delay);
      };
      tick();
    }, 400);
  }

  stopHold() {
    if (this.holdTimeout) {
      clearTimeout(this.holdTimeout);
      this.holdTimeout = null;
    }
    if (this.holdInterval) {
      clearTimeout(this.holdInterval);
      this.holdInterval = null;
    }
  }

  private changeCarga(index: number, delta: number) {
    const ctrl = this.exerciciosFA.at(index).get('cargaEfetuada');
    if (!ctrl) return;

    const current = Number(ctrl.value) || 0;
    const min = this.getMinCarga(index);
    const max = this.getMaxCarga(index);

    let next = current + delta;
    next = Math.round(next * 2) / 2; // múltiplos de 0.5
    next = Math.min(max, Math.max(min, next));

    if (next === current) {
      // chegou no limite → cancela hold
      this.stopHold();
      return;
    }

    ctrl.setValue(next, { emitEvent: true });
  }

  salvar() {

    const payload = {
      treinoId: this.treinoId,
      usuarioId: this.usuarioId,
      dataExecucao: new Date(),

      exercicios: this.form.value.exercicios.map((e: any) => ({
        exercicioId: e.exercicioId,
        cargaEfetuada: parseFloat(e.cargaEfetuada)
      })),

      abdominais: this.form.value.abdominais,

      cardios: this.form.value.cardios.map((c: any) => ({
        ...c,
        distanciaKm: parseFloat(c.distanciaKm),
        tempoMinutos: parseInt(c.tempoMinutos)
      }))
    };

    this.treinoUsuarioService.salvarExecucao(payload)
      .subscribe({
        next: () => {
          this.toastService.show('Treino salvo com sucesso', { type: 'success' });
          localStorage.removeItem(this.storageKey);
        },
        error: () => {
          this.toastService.show('Erro ao salvar treino', { type: 'danger' });
        }
      });
  }
}